import { createContext, useCallback, useContext, type ReactNode } from "react";

import { callReopenCommandOnFormPaymentError } from "./callReopenCommandOnFormPaymentError";
import type { PaymentErrorReport } from "./helper";
import { usePaymentErrorHandler } from "./usePaymentErrorHandler";

import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { trpc } from "@/common/helpers/trpc";

export interface PaymentErrorContextType {
  paymentError: string | null;
  handlePaymentError: (report: PaymentErrorReport) => void;
  clearPaymentError: () => void;
  isBankConnectionGuidanceVisible: boolean;
  showBankConnectionGuidance: () => void;
}

export const PaymentErrorContext = createContext<PaymentErrorContextType>({
  paymentError: null,
  handlePaymentError: () => {},
  clearPaymentError: () => {},
  isBankConnectionGuidanceVisible: false,
  showBankConnectionGuidance: () => {},
});

export const PaymentErrorProvider = ({ children }: { children: ReactNode }) => {
  const { mutateAsync: reOpenCommandAsync } = trpc.reOpenCommand.useMutation();
  const onReopenCommandAfterFormPaymentError = useCallback(
    (report: PaymentErrorReport) => {
      void callReopenCommandOnFormPaymentError(reOpenCommandAsync, report);
    },
    [reOpenCommandAsync]
  );
  const {
    formData: { id: formId },
    commandId,
    organization,
    setIsSubmitting,
    setIsPadLoading,
    formAmplitudeContext,
    formType,
    tip,
    selectedPaymentMethod,
    isEmbed,
    stripeRecurrenceInterval,
    currency,
    category,
    prioritizeBankPayment,
    expressCheckoutDisabled,
    paymentMode,
  } = useContext(FrontendFormContext);

  const value = usePaymentErrorHandler({
    formId,
    commandId: commandId ?? null,
    organization,
    setIsSubmitting,
    setIsPadLoading,
    formAmplitudeContext,
    formType,
    tip,
    selectedPaymentMethod,
    isEmbed,
    stripeRecurrenceInterval,
    currency,
    category,
    prioritizeBankPayment,
    expressCheckoutDisabled,
    paymentMode,
    onPaymentError: onReopenCommandAfterFormPaymentError,
  });

  return <PaymentErrorContext.Provider value={value}>{children}</PaymentErrorContext.Provider>;
};

export const usePaymentErrorContext = (): PaymentErrorContextType => {
  return useContext(PaymentErrorContext);
};
